import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CloudArrowUpIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { TargetBox } from 'components/DragDrop'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { AccountVerificationType, BrokerIdentifyCategory } from 'pages/Admin'
import React, { useEffect, useMemo, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import {
  deleteBrokerVerificationTemplateDocument,
  getBrokerVerificationTemplateByUserType,
  openS3Document,
  StripeIntentStatus,
  updateBrokerVerificationTemplateDocument,
  uploadFiles,
} from 'services'
import { getBrokerVerificationCategories, setBrokerVerificationCategories } from 'services/apis/admin'
import { ButtonGroup, Select } from 'stories/components'
import { confirm, formatTime } from 'utils'

import {
  BrokerVerificationDocument,
  VerificationCategoryStatusOptions,
  VerificationStatus,
} from '../../../ManageAccounts/BrokerVerificationForUser'

export interface IBrokerPaymentHistory {
  id: number
  paymentId: string
  amount: number
  title: string
  requestType: string
  intentId: string
  message: string
  method: string // link | pay
  payerEmail: string
  createdBy: string
  status: StripeIntentStatus
  createdAt: Date
}

export const BrokerVerificationCategory = () => {
  const profile = useSelector((state: any) => state.auth.profile)
  const [isLoading, setLoading] = useState(true)
  const [documents, setDocuments] = useState<BrokerVerificationDocument[]>([])
  const [verificationCategories, setVerificationCategories] = useState<
    Record<AccountVerificationType, BrokerIdentifyCategory[]>
  >({
    broker: [],
    correspondent: [],
    tablefunder: [],
    contractorApproval: [],
  })
  const [type, setType] = useState('broker')
  const tabs: Record<string, string> = {
    broker: 'Broker',
    correspondent: 'Correspondent',
    tablefunder: 'Table Funder',
  }

  useEffect(() => {
    refetch()
  }, [type])

  const loadVerificationCategories = async () => {
    const values = await Promise.all(
      [AccountVerificationType.Broker, AccountVerificationType.Correspondent, AccountVerificationType.Tablefunder].map(
        (accountType) => getBrokerVerificationCategories(accountType),
      ),
    )
    setVerificationCategories({
      broker: values[0],
      correspondent: values[1],
      tablefunder: values[2],
      contractorApproval: [],
    })
  }

  const categories = useMemo(() => {
    return verificationCategories[type as AccountVerificationType]
  }, [verificationCategories, type])

  const sameVerificationCategories = useMemo(() => {
    let rlt: any = []
    let part: any = []
    const data: any = cloneDeep(verificationCategories)
    Object.keys(data).map((key) => {
      const items: any = data[key]
      items.map((item: any) => {
        if (!part.includes(item.id)) {
          let group = [item.id]
          part.push(item.id)
          Object.keys(data).map((key1) => {
            if (key1 !== key) {
              const items1 = data[key1]
              items1.map((item1: any) => {
                if (!part.includes(item1.id)) {
                  if (item.value?.toLowerCase().trim() === item1.value?.toLowerCase().trim()) {
                    group.push(item1.id)
                    part.push(item1.id)
                  }
                }
              })
            }
          })
          rlt.push(group)
        }
      })
    })
    return rlt
  }, [verificationCategories])

  const refetch = async () => {
    setLoading(true)
    const res = await getBrokerVerificationTemplateByUserType(type)
    setDocuments(res)
    await loadVerificationCategories()
    setLoading(false)
  }

  const updateCategoriesLocal = (next: BrokerIdentifyCategory[]) => {
    setVerificationCategories((prev) => ({
      ...prev,
      [type]: next,
    }))
  }

  const submitCategories = async (next: BrokerIdentifyCategory[]) => {
    setLoading(true)
    const savingValues = next.filter((value) => value.value !== null)
    await setBrokerVerificationCategories(savingValues, type as AccountVerificationType)
    updateCategoriesLocal(savingValues)
    setLoading(false)
  }

  const onAddCategory = () => {
    const newValues = cloneDeep(categories)
    newValues.push({ id: `${Date.now()}`, value: null, visible: false })
    updateCategoriesLocal(newValues)
  }

  const onUpdateCategoryName = (newValue: string, index: number) => {
    const newValues = cloneDeep(categories)
    newValues[index].value = newValue
    updateCategoriesLocal(newValues)
    submitCategories(newValues)
  }

  const onChangeCategoryOrder = (oldIdx: number, newIdx: number) => {
    const newValues = cloneDeep(categories)
    const deletedItem = newValues.splice(oldIdx, 1)
    newValues.splice(newIdx, 0, ...deletedItem)
    updateCategoriesLocal(newValues)
    submitCategories(newValues)
  }

  const onToggleCategoryVisible = (index: number) => {
    const newValues = cloneDeep(categories)
    newValues[index].visible = !newValues[index].visible
    updateCategoriesLocal(newValues)
    submitCategories(newValues)
  }

  const onDeleteCategory = async (index: number) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure want to delete this category?
        <br />
        <span className="text-gray-600">Category: {categories[index].value}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newValues = cloneDeep(categories)
    newValues.splice(index, 1)
    updateCategoriesLocal(newValues)
    await submitCategories(newValues)
  }

  const getCategoryStatus = (categoryId: string) => {
    const categoryDoc = documents.find((doc) => doc.category == categoryId && !doc.fileName)
    if (!categoryDoc) return ''
    return categoryDoc.status
  }

  const onChangeCategoryStatus = async (categoryId: string, status: VerificationStatus) => {
    const index = documents.findIndex((doc) => doc.category == categoryId && !doc.fileKey)
    let newDocs = cloneDeep(documents)
    let newDoc: BrokerVerificationDocument
    if (index === -1) {
      newDoc = {
        id: 0,
        category: categoryId,
        status,
        fileKey: '',
        fileName: '',
        createdAt: 'now',
        email: '',
      }
      newDocs.push(newDoc)
    } else {
      newDocs[index].status = status
      newDoc = newDocs[index]
    }

    let categoryName = ''
    if (categoryId != Number(categoryId).toString()) categoryName = categoryId
    else {
      const category = categories.find((item) => item.id == categoryId)
      if (!category || !category.value) return
      categoryName = category.value
    }
    newDoc.categoryName = categoryName

    setLoading(true)
    newDocs = await updateBrokerVerificationTemplateDocument(type, [newDoc])
    setDocuments(newDocs)

    setLoading(false)
  }

  const onRemoveDocument = async (document: BrokerVerificationDocument) => {
    const index = documents.findIndex((doc) => doc.id === document.id)
    if (index === -1) return

    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure want to delete this document?
        <br />
        <span className="text-gray-600">Document: {document.fileName}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setLoading(true)
    await deleteBrokerVerificationTemplateDocument(type, [document.id])
    const newDocs = cloneDeep(documents)
    newDocs.splice(index, 1)
    setDocuments(newDocs)
    setLoading(false)
  }

  const onUploadFilesFromInput = (event: React.ChangeEvent<HTMLInputElement>, category: string) => {
    const { files } = event.target
    if (!files || files.length == 0) return
    const arrFile: Array<File> = []
    for (let i = 0; i < files.length; i++) arrFile.push(files[i])

    onUploadFiles(arrFile, category)
  }

  const onDrop = async (doc: any, category: string) => {
    onUploadFiles(doc.files, category)
  }

  const onUploadFiles = async (files: any, category: string) => {
    if (!files || !files.length) return
    const fileArr: File[] = []
    const fileLen = files.length
    for (let i = 0; i < fileLen; i++) fileArr.push(files[i])
    const data = {
      path: `brokerVerify/${type}`,
    }
    setLoading(true)
    uploadFiles(data, fileArr)
      .then(async (keys: string[]) => {
        const fileList: BrokerVerificationDocument[] = []
        for (let i = 0; i < fileLen; i++) {
          fileList.push({
            id: 0,
            email: profile.email,
            status: VerificationStatus.Pending,
            category,
            fileName: fileArr[i].name,
            fileKey: keys[i],
            createdAt: formatTime(),
          })
        }
        const newDocs = await updateBrokerVerificationTemplateDocument(type, fileList)
        setDocuments(newDocs)
      })
      .finally(() => setLoading(false))
  }

  const onUpdateDocumentProp = async (key: string, newValue: string, id: number) => {
    const index = documents.findIndex((doc) => doc.id == id)
    if (index == -1) return

    if (key == 'fileName') newValue = newValue + documents[index].fileName.substr(-4)

    const newDoc = {
      ...documents[index],
      [key]: newValue,
    }
    setLoading(true)

    const newDocuments = cloneDeep(documents)
    ;(newDocuments[index] as any)[key] = newValue
    setDocuments(newDocuments)

    const newDocs = await updateBrokerVerificationTemplateDocument(type, [newDoc])
    setDocuments(newDocs)
    setLoading(false)
  }

  const sortKeys = Object.keys(Array(categories.length).fill(1))

  const renderSubDocuments = (filteredCategory: string) => {
    const isSignCategory = filteredCategory === 'sign'

    let group = [filteredCategory]
    sameVerificationCategories.map((item: any) => {
      if (item.includes(filteredCategory)) group = item
    })

    const properDocs = documents
      .filter(({ category, fileName }) => group.includes(category) && fileName)
      .sort((a, b) => (!isSignCategory && a.id > b.id ? -1 : 1))

    return properDocs.map((document, index: number) => (
      <tr className="hover:bg-slate-100" key={index}>
        <td className="px-6 min-w-[300px]" colSpan={2}>
          <div className="flex gap-1 items-center">
            <div className="w-[18px]">{index + 1}.</div>

            <PlainInput
              value={document.fileName.substring(0, document.fileName.length - 4)}
              content={document.fileName}
              onChange={(newName: string) => onUpdateDocumentProp('fileName', newName, document.id)}
            />
          </div>
        </td>
        <td className="px-2 w-[160px]">{document.email}</td>
        <td className="px-2 w-[138px]">{formatTime(document.createdAt)}</td>
        <td className="px-2 py-1 w-[120px]">
          <button
            className="text-shade-blue p-1 hover-shadow1 cursor-pointer rounded"
            onClick={() => openS3Document(document.fileKey)}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </button>
          <button
            className="text-red-800 p-1 hover-shadow1 cursor-pointer rounded"
            onClick={() => onRemoveDocument(document)}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </td>
      </tr>
    ))
  }

  const renderCategory = (category: BrokerIdentifyCategory, index: number) => {
    const { visible } = category
    const renderTable = () => {
      return (
        <table className="w-full">
          <tbody>
            <tr className="">
              <td className="px-2 py-1 min-w-[280px]">
                <div className="flex items-center font-medium gap-2">
                  <span className="w-[20px] shrink-0">{index + 1}.</span>
                  <div className="flex-1 min-w-[160px]">
                    <PlainInput
                      value={category.value || ''}
                      content={category.value || ''}
                      className="h-[35px]"
                      onChange={(newValue: string) => onUpdateCategoryName(newValue, index)}
                    />
                  </div>
                  {!visible && <span className="text-xs text-gray-400 shrink-0">(Invisible)</span>}
                </div>
              </td>
              <td className="px-2 py-1 w-[70px]">
                <select
                  value={index}
                  className="text-sm border-gray-200 py-1"
                  onChange={(e) => onChangeCategoryOrder(index, parseInt(e.target.value))}
                >
                  {[...sortKeys].map((value) => (
                    <option value={value} key={value}>
                      {parseInt(value) + 1}
                    </option>
                  ))}
                </select>
              </td>
              <td className="px-2 py-1 w-[160px]"></td>
              <td className="px-2 py-1 w-[138px]"></td>
              <td className="px-2 py-1 w-[120px]">
                <div className="flex items-center gap-1">
                  <span
                    className="text-shade-blue p-1 hover-shadow1 cursor-pointer rounded"
                    onClick={() => onToggleCategoryVisible(index)}
                  >
                    {visible ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="text-gray-300 w-4 h-4" />
                    )}
                  </span>
                  <span
                    className="text-red-800 p-1 hover-shadow1 cursor-pointer rounded"
                    onClick={() => onDeleteCategory(index)}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </span>
                  <label
                    className="text-gray-900 p-1 hover-shadow1 cursor-pointer inline-block rounded"
                    htmlFor={`file-${category.id}`}
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                  </label>
                  <input
                    className="hidden"
                    id={`file-${category.id}`}
                    type="file"
                    accept=".pdf,.jpg"
                    multiple
                    required
                    onChange={(event) => onUploadFilesFromInput(event, category.id)}
                  />
                </div>
              </td>
            </tr>
            {renderSubDocuments(category.id)}
          </tbody>
        </table>
      )
    }
    let bgCn = index % 2 ? 'bg-gray-50' : ''
    return (
      <React.Fragment key={category.id}>
        <tr className={bgCn}>
          <td colSpan={5} className="border-b overflow-auto">
            <TargetBox types={[NativeTypes.FILE]} onDrop={(docs: any) => onDrop(docs, category.id)}>
              {renderTable()}
            </TargetBox>
          </td>

          <td className="px-2 py-1 border-b align-baseline pt-2">
            <Select
              id={`category-status-${category.id}`}
              size={3}
              className="-mb-4 bg-white w-[120px]"
              options={VerificationCategoryStatusOptions}
              value={getCategoryStatus(category.id)}
              onChange={(value) => onChangeCategoryStatus(category.id, value)}
            />
          </td>
        </tr>
      </React.Fragment>
    )
  }

  const renderSignCategory = () => {
    const index = categories.length

    return (
      <>
        <tr></tr>
        <tr className="border-gray-300 border-t-[3px]">
          <td colSpan={5} className="border-b overflow-auto">
            <table className="w-full">
              <tbody>
                <tr className="">
                  <td className="px-2 py-3" colSpan={5}>
                    <div className="flex items-center font-medium">
                      <span className="w-[20px]">{index + 1}.</span>{' '}
                      <span className="text-[15px]">{tabs[type]} Agreement</span>
                    </div>
                  </td>
                </tr>
                {renderSubDocuments('sign')}
              </tbody>
            </table>
          </td>

          <td className="px-2 w-30 border-b align-baseline pt-2">
            <Select
              id={`category-status-sign`}
              size={3}
              className="bg-white w-[120px]"
              options={VerificationCategoryStatusOptions}
              value={getCategoryStatus('sign')}
              onChange={(value) => onChangeCategoryStatus('sign', value)}
            />
          </td>
        </tr>
      </>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl font-bold flex items-center mb-3">Third party Approval Categories</h2>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <ButtonGroup title={tabs} value={type} onChange={setType} />
        <button
          type="button"
          className="font-variation-settings-600 text-sm font-medium text-shade-blue hover:underline"
          onClick={onAddCategory}
        >
          + Add
        </button>
      </div>
      <div>
        <div className="relative bg-white border rounded mb-6 p-4">
          <DndProvider backend={HTML5Backend}>
            <div className="relative overflow-auto mb-3 shadow-md sm:rounded-lg">
              <div className="absolute top-1 right-[calc(50%-135px)] w-full h-fit pointer-events-none">
                <div className="absolute top-0 right-0 border-dashed border-2 border-gray-300 px-4 pt-1 text-gray-300 w-[270px] text-center">
                  <CloudArrowUpIcon className="w-10 h-10 mx-auto" />
                  <span>Drag and drop files.</span>
                </div>
              </div>
              <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
                <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-3 min-w-[280px]">Category Name</th>
                    <th className="px-2 py-3 w-[70px]">Order</th>
                    <th className="px-2 py-3 w-[160px]">Uploaded By</th>
                    <th className="px-2 py-3 w-[138px]">Uploaded At</th>
                    <th className="px-2 py-3 w-[120px]">Actions</th>
                    <th className="px-2 py-3 w-[150px]">Category Status</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category: BrokerIdentifyCategory, index: number) => renderCategory(category, index))}
                  {renderSignCategory()}
                </tbody>
              </table>
            </div>
          </DndProvider>
        </div>
      </div>
    </div>
  )
}
